/**
 * ReservationInfo Variants - A/B Testing System
 * 
 * Usage:
 *   import { ReservationInfoWithVariant } from './reservation-info-variants';
 *   <ReservationInfoWithVariant reserva={...} variant="B" />
 * 
 * Or use auto-detection:
 *   <ReservationInfoWithVariant reserva={...} variant="auto" />
 */

export * from './types';
export { VariantA } from './VariantA';
export { VariantB } from './VariantB';

import { VariantA } from './VariantA';
import { VariantB } from './VariantB';
import type { ReservationInfoVariantProps, VariantKey } from './types';

interface ReservationInfoWithVariantProps extends ReservationInfoVariantProps {
  /** 
   * Variant to display:
   * - 'A': Full layout (original, desktop-optimized)
   * - 'B': Compact layout (mobile-optimized)
   * - 'auto': Auto-detect based on screen size
   * - 'C', 'D': Future variants
   */
  variant?: VariantKey | 'auto';
}

/**
 * Main component that renders the appropriate variant
 * Supports A/B testing by passing different variant props
 */
export function ReservationInfoWithVariant({ 
  reserva, 
  variant = 'auto',
  onInteraction 
}: ReservationInfoWithVariantProps) {
  // Auto-detect: use compact on mobile
  const effectiveVariant = variant === 'auto' 
    ? (typeof window !== 'undefined' && window.innerWidth < 768 ? 'B' : 'A')
    : variant;

  switch (effectiveVariant) {
    case 'A':
      return <VariantA reserva={reserva} onInteraction={onInteraction} />;
    case 'B':
      return <VariantB reserva={reserva} onInteraction={onInteraction} />;
    case 'C':
    case 'D':
      // Future variants - fallback to B for now
      console.warn(`Variant ${effectiveVariant} not implemented yet, using B`);
      return <VariantB reserva={reserva} onInteraction={onInteraction} />;
    default:
      return <VariantA reserva={reserva} onInteraction={onInteraction} />;
  }
}

/**
 * Hook for A/B test assignment
 * Can be extended to integrate with analytics/feature flags
 */
export function useVariantAssignment(tokenOrUserId?: string): VariantKey {
  // Simple deterministic assignment based on token
  // In production, this could integrate with a feature flag service
  if (!tokenOrUserId) return 'A';
  
  // Hash the token to get consistent assignment
  const hash = tokenOrUserId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return hash % 2 === 0 ? 'A' : 'B';
}
